/**
 * Core Grid Operations
 * Create, query, and manipulate rectangular grid maps.
 */

import type { GridMap, Cell, TerrainType, CardinalDirection, Entity } from './types';

// ============================================================================
// CONSTANTS
// ============================================================================

const OPPOSITE: Record<CardinalDirection, CardinalDirection> = {
  N: 'S', S: 'N', E: 'W', W: 'E',
};

const DELTAS: Record<CardinalDirection, { dx: number; dy: number }> = {
  N: { dx: 0, dy: -1 },
  S: { dx: 0, dy: 1 },
  E: { dx: 1, dy: 0 },
  W: { dx: -1, dy: 0 },
};

// ============================================================================
// GRID CREATION
// ============================================================================

export function createGrid(width: number, height: number, fill: TerrainType = 'wall'): GridMap {
  const cells: Cell[][] = [];
  for (let y = 0; y < height; y++) {
    const row: Cell[] = [];
    for (let x = 0; x < width; x++) {
      row.push({
        x, y, terrain: fill,
        walls: { N: false, E: false, S: false, W: false },
      });
    }
    cells.push(row);
  }
  return { width, height, cells, entities: [] };
}

// ============================================================================
// CELL ACCESS
// ============================================================================

export function inBounds(map: GridMap, x: number, y: number): boolean {
  return x >= 0 && x < map.width && y >= 0 && y < map.height;
}

export function getCell(map: GridMap, x: number, y: number): Cell | null {
  if (!inBounds(map, x, y)) return null;
  return map.cells[y][x];
}

// ============================================================================
// TERRAIN
// ============================================================================

export function setTerrain(map: GridMap, x: number, y: number, terrain: TerrainType): void {
  if (inBounds(map, x, y)) {
    map.cells[y][x].terrain = terrain;
  }
}

// ============================================================================
// WALLS (with adjacency invariant)
// ============================================================================

/**
 * Set a wall on a cell edge. Automatically sets the opposite wall on the
 * neighboring cell to maintain the adjacency invariant.
 */
export function setWall(map: GridMap, x: number, y: number, dir: CardinalDirection, hasWall: boolean): void {
  const cell = getCell(map, x, y);
  if (!cell) return;
  cell.walls[dir] = hasWall;

  const d = DELTAS[dir];
  const neighbor = getCell(map, x + d.dx, y + d.dy);
  if (neighbor) {
    neighbor.walls[OPPOSITE[dir]] = hasWall;
  }
}

export function toggleWall(map: GridMap, x: number, y: number, dir: CardinalDirection): void {
  const cell = getCell(map, x, y);
  if (!cell) return;
  setWall(map, x, y, dir, !cell.walls[dir]);
}

// ============================================================================
// COLLISION & LIGHT
// ============================================================================

/**
 * Check if movement from (x,y) in direction dir is blocked.
 * Blocked by: wall edges, out of bounds, solid terrain at destination.
 * Closed doors (not in openDoors) also block movement.
 */
export function isBlocked(map: GridMap, x: number, y: number, dir: CardinalDirection, openDoors?: Set<string>): boolean {
  const cell = getCell(map, x, y);
  if (!cell) return true;

  // Wall edge on departure side
  if (cell.walls[dir]) return true;

  const d = DELTAS[dir];
  const nx = x + d.dx;
  const ny = y + d.dy;

  // Out of bounds
  if (!inBounds(map, nx, ny)) return true;

  // Solid terrain at destination
  const dest = map.cells[ny][nx];
  if (dest.terrain === 'wall' || dest.terrain === 'void') return true;

  // Closed doors block movement
  if (dest.terrain === 'door' && !openDoors?.has(coordKey(nx, ny))) return true;

  return false;
}

/** Check if a cell blocks line of sight (for shadowcasting). Open doors don't block light. */
export function blocksLight(map: GridMap, x: number, y: number, openDoors?: Set<string>): boolean {
  if (!inBounds(map, x, y)) return true;
  const terrain = map.cells[y][x].terrain;
  if (terrain === 'wall' || terrain === 'void') return true;
  if (terrain === 'door' && !openDoors?.has(coordKey(x, y))) return true;
  return false;
}

// ============================================================================
// NEIGHBOR UTILITIES
// ============================================================================

export function neighbor(x: number, y: number, dir: CardinalDirection): { x: number; y: number } {
  const d = DELTAS[dir];
  return { x: x + d.dx, y: y + d.dy };
}

export function getNeighbors(map: GridMap, x: number, y: number): Array<{ x: number; y: number; dir: CardinalDirection }> {
  const result: Array<{ x: number; y: number; dir: CardinalDirection }> = [];
  for (const dir of ['N', 'E', 'S', 'W'] as CardinalDirection[]) {
    const n = neighbor(x, y, dir);
    if (inBounds(map, n.x, n.y)) {
      result.push({ x: n.x, y: n.y, dir });
    }
  }
  return result;
}

// ============================================================================
// ENTITY MANAGEMENT
// ============================================================================

export function addEntity(map: GridMap, entity: Entity): GridMap {
  return { ...map, entities: [...map.entities, entity] };
}

export function removeEntity(map: GridMap, entityId: string): GridMap {
  return { ...map, entities: map.entities.filter(e => e.id !== entityId) };
}

export function getEntity(map: GridMap, entityId: string): Entity | undefined {
  return map.entities.find(e => e.id === entityId);
}

export function moveEntity(map: GridMap, entityId: string, x: number, y: number): GridMap {
  return {
    ...map,
    entities: map.entities.map(e => e.id === entityId ? { ...e, x, y } : e),
  };
}

// ============================================================================
// CARVING (for generators)
// ============================================================================

/** Carve a rectangular room — set all cells inside to floor. */
export function carveRoom(map: GridMap, rx: number, ry: number, rw: number, rh: number): void {
  for (let y = ry; y < ry + rh && y < map.height; y++) {
    for (let x = rx; x < rx + rw && x < map.width; x++) {
      if (inBounds(map, x, y)) {
        map.cells[y][x].terrain = 'floor';
      }
    }
  }
}

/** Carve an L-shaped corridor between two points. */
export function carveCorridor(
  map: GridMap,
  x1: number, y1: number,
  x2: number, y2: number,
  horizontalFirst: boolean,
): void {
  if (horizontalFirst) {
    // Horizontal then vertical
    const startX = Math.min(x1, x2);
    const endX = Math.max(x1, x2);
    for (let x = startX; x <= endX; x++) {
      setTerrain(map, x, y1, 'floor');
    }
    const startY = Math.min(y1, y2);
    const endY = Math.max(y1, y2);
    for (let y = startY; y <= endY; y++) {
      setTerrain(map, x2, y, 'floor');
    }
  } else {
    // Vertical then horizontal
    const startY = Math.min(y1, y2);
    const endY = Math.max(y1, y2);
    for (let y = startY; y <= endY; y++) {
      setTerrain(map, x1, y, 'floor');
    }
    const startX = Math.min(x1, x2);
    const endX = Math.max(x1, x2);
    for (let x = startX; x <= endX; x++) {
      setTerrain(map, x, y2, 'floor');
    }
  }
}

// ============================================================================
// COORDINATE KEYS
// ============================================================================

export function coordKey(x: number, y: number): string {
  return `${x},${y}`;
}

export function parseCoordKey(key: string): { x: number; y: number } {
  const [x, y] = key.split(',').map(Number);
  return { x, y };
}
